import { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import {
  Check, ChevronDown, ChevronUp, Columns3, Handshake, List, Plus, Search, Sparkles, Trophy, X, Clock,
  ArrowRight, Settings2, Users,
} from "lucide-react"
import { toast } from "@/components/portal/Toast"
import { DealBlatt } from "@/components/vertrieb/DealBlatt"
import { KontaktBlatt, KontaktWahl, KontakteAnsicht, NeuerKontakt, QUELLEN, useKontakte } from "@/components/vertrieb/Kontakte"
import { PhasenEditor } from "@/components/vertrieb/PhasenEditor"
import {
  type Board, type Deal, type Erinnerung, type Kontakt, type Stufe,
  crmFetch, euro, heuteIso, initialen, naechsterTermin, personFarbe, plusTage, tageSeit, tagKurz,
} from "@/lib/crm"
import "./vertrieb.css"

/**
 * Vertrieb — die eigene Pipeline von werkXpartner (Paul 26.10.2026).
 *
 * „a la hubspot … mit pipeline und allem", dazu Mails über kontakt@ und
 * Erinnerungen, die „im CRM-Reiter aufpoppen". Und: „richtig simpel und
 * ansprechend".
 *
 * Aufbau von oben nach unten: vier Zahlen → „Heute dran" (klappt beim Öffnen
 * auf, wenn etwas fällig ist) → Board. Ein Klick auf eine Karte öffnet das
 * Seitenblatt mit allem zum Deal; das Board bleibt dahinter stehen.
 *
 * 🔑 Erinnerungen sind berechnet, keine angelegten Aufgaben (B9). Wer am
 * Deal etwas tut, lässt sie verschwinden.
 */

const ANSICHT_SPEICHER = "vertrieb.ansicht"
const FOKUS_SPEICHER = "vertrieb.fokus-zu"

function lies(k: string): string | null {
  try { return localStorage.getItem(k) } catch { return null }
}
function schreib(k: string, v: string) {
  try { localStorage.setItem(k, v) } catch { /* privat */ }
}

export default function VertriebPage() {
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const offenerDeal = Number(params.get("deal")) || null
  const offenerKontakt = Number(params.get("kontakt")) || null
  const reiter: "pipeline" | "kontakte" = params.get("tab") === "kontakte" ? "kontakte" : "pipeline"
  const [phasenOffen, setPhasenOffen] = useState(false)
  const [neuerKontakt, setNeuerKontakt] = useState(false)
  const [ansicht, setAnsicht] = useState<"board" | "liste">(lies(ANSICHT_SPEICHER) === "liste" ? "liste" : "board")
  const [nurMeine, setNurMeine] = useState(false)
  const [suche, setSuche] = useState("")
  // „Neuer Deal": Stufe und ggf. schon gewählter Kontakt
  const [neu, setNeu] = useState<{ stufe: string; kontakt?: Kontakt } | null>(null)

  const boardQ = useQuery<Board>({
    queryKey: ["crm-board"],
    queryFn: () => crmFetch<Board>("/board"),
    refetchInterval: 60_000,
  })

  // Immer nur EIN Blatt offen: Deal oder Kontakt.
  const oeffneBlatt = (art: "deal" | "kontakt", id: number | null) => {
    const p = new URLSearchParams(params)
    p.delete("deal"); p.delete("kontakt")
    if (id) p.set(art, String(id))
    setParams(p, { replace: !id })
  }
  const oeffne = (id: number | null) => oeffneBlatt("deal", id)
  const oeffneKontakt = (id: number | null) => oeffneBlatt("kontakt", id)
  const wechsleReiter = (r: "pipeline" | "kontakte") => {
    const p = new URLSearchParams(params)
    if (r === "kontakte") p.set("tab", "kontakte")
    else p.delete("tab")
    setParams(p)
    setSuche("")
  }
  const kontakteQ = useKontakte()

  const aendern = useMutation({
    mutationFn: ({ id, daten }: { id: number; daten: Partial<Deal> }) =>
      crmFetch<Deal>(`/deals/${id}`, { method: "PATCH", json: daten }),
    onMutate: async ({ id, daten }) => {
      await qc.cancelQueries({ queryKey: ["crm-board"] })
      const vorher = qc.getQueryData<Board>(["crm-board"])
      if (vorher) {
        qc.setQueryData<Board>(["crm-board"], {
          ...vorher,
          deals: vorher.deals.map((d) => (d.id === id ? { ...d, ...daten, stufe_seit: daten.stufe ? new Date().toISOString() : d.stufe_seit } : d)),
        })
      }
      return { vorher }
    },
    onError: (e: Error, _v, ctx) => {
      if (ctx?.vorher) qc.setQueryData(["crm-board"], ctx.vorher)
      toast(e.message, "error")
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["crm-board"] })
      qc.invalidateQueries({ queryKey: ["crm-faellig"] })
    },
  })

  const board = boardQ.data
  const stufen = board?.stufen || []
  const stufeVon = useMemo(() => Object.fromEntries(stufen.map((s) => [s.schluessel, s])), [stufen])

  const sichtbare = useMemo(() => {
    const q = suche.trim().toLowerCase()
    return (board?.deals || []).filter((d) => {
      if (nurMeine && d.zustaendig !== board?.ich) return false
      if (!q) return true
      return [d.titel, d.ansprechpartner, d.email, d.telefon, d.quelle].some((x) => (x || "").toLowerCase().includes(q))
    })
  }, [board, suche, nurMeine])

  const erinnerungen = useMemo(
    () => (board?.erinnerungen || []).filter((e) => !nurMeine || e.zustaendig === board?.ich),
    [board, nurMeine],
  )
  const dringendIds = useMemo(
    () => new Set(erinnerungen.filter((e) => e.schwere === "rot").map((e) => e.deal_id)),
    [erinnerungen],
  )

  if (boardQ.isLoading) return <div className="vt"><div className="vt-hinweis" style={{ padding: 24 }}>Lädt …</div></div>
  if (boardQ.error) return <div className="vt"><div className="vt-warnung">{(boardQ.error as Error).message}</div></div>

  const offeneStufen = stufen.filter((s) => s.art !== "verloren")
  const keineDeals = (board?.deals.length || 0) === 0

  return (
    <div className="vt">
      <div className="vt-kopf">
        <div className="vt-titel">
          <h2>Vertrieb</h2>
          <p>Unsere Leads, Kontakte und Deals – vom ersten Gespräch bis zum Start.</p>
          <div className="vt-tabs" role="tablist">
            <button role="tab" aria-selected={reiter === "pipeline"} className={reiter === "pipeline" ? "an" : ""}
              onClick={() => wechsleReiter("pipeline")}><Columns3 /> Pipeline</button>
            <button role="tab" aria-selected={reiter === "kontakte"} className={reiter === "kontakte" ? "an" : ""}
              onClick={() => wechsleReiter("kontakte")}>
              <Users /> Kontakte {kontakteQ.data ? <span className="vt-anzahl">{kontakteQ.data.items.length}</span> : null}
            </button>
          </div>
        </div>
        <div className="vt-werkzeuge">
          <label className="vt-suche">
            <Search />
            <input value={suche} onChange={(e) => setSuche(e.target.value)}
              placeholder={reiter === "kontakte" ? "Kontakt suchen" : "Deal suchen"} aria-label="Suchen" />
          </label>
          {reiter === "pipeline" ? (
            <>
              <div className="vt-segment" role="group" aria-label="Zuständigkeit">
                <button className={!nurMeine ? "an" : ""} onClick={() => setNurMeine(false)}>Alle</button>
                <button className={nurMeine ? "an" : ""} onClick={() => setNurMeine(true)}>Meine</button>
              </div>
              <div className="vt-segment" role="group" aria-label="Ansicht">
                <button className={ansicht === "board" ? "an" : ""} title="Board"
                  onClick={() => { setAnsicht("board"); schreib(ANSICHT_SPEICHER, "board") }}><Columns3 /></button>
                <button className={ansicht === "liste" ? "an" : ""} title="Liste"
                  onClick={() => { setAnsicht("liste"); schreib(ANSICHT_SPEICHER, "liste") }}><List /></button>
              </div>
              <button className="vt-knopf leise" onClick={() => setPhasenOffen(true)} title="Phasen umbenennen, löschen, hinzufügen">
                <Settings2 /> Phasen
              </button>
              <button className="vt-knopf haupt" onClick={() => setNeu({ stufe: "lead" })}><Plus /> Neuer Deal</button>
            </>
          ) : (
            <button className="vt-knopf haupt" onClick={() => setNeuerKontakt(true)}><Plus /> Neuer Kontakt</button>
          )}
        </div>
      </div>

      {reiter === "kontakte" && board ? (
        <KontakteAnsicht suche={suche} board={board} oeffne={(id) => oeffneKontakt(id)} neu={() => setNeuerKontakt(true)} />
      ) : keineDeals ? (
        <div className="vt-leer">
          <div className="vt-leer-bild"><Handshake /></div>
          <h3>Noch keine Deals</h3>
          <p>Leg den ersten Lead an – Firma reicht. Alles andere kannst du später ergänzen.</p>
          <button className="vt-knopf haupt" onClick={() => setNeu({ stufe: "lead" })}><Plus /> Ersten Deal anlegen</button>
        </div>
      ) : (
        <>
          <Kennzahlen deals={sichtbare} stufeVon={stufeVon} erinnerungen={erinnerungen} />
          <HeuteDran erinnerungen={erinnerungen} oeffne={oeffne} />
          {ansicht === "board" ? (
            <BoardAnsicht
              stufen={offeneStufen}
              deals={sichtbare}
              dringend={dringendIds}
              oeffne={oeffne}
              verschiebe={(id, stufe) => {
                const ziel = stufeVon[stufe]
                aendern.mutate({ id, daten: { stufe } })
                if (ziel?.art === "gewonnen") toast("Gewonnen – stark! 🎉", "success")
              }}
              neuIn={(s) => setNeu({ stufe: s })}
            />
          ) : (
            <ListenAnsicht deals={sichtbare} stufeVon={stufeVon} oeffne={oeffne} />
          )}
        </>
      )}

      {neu && board && (
        <NeuerDeal
          stufe={neu.stufe}
          kontakt={neu.kontakt}
          stufen={offeneStufen}
          ich={board.ich}
          zu={() => setNeu(null)}
          angelegt={(d) => { setNeu(null); oeffne(d.id) }}
        />
      )}
      {neuerKontakt && board && (
        <NeuerKontakt ich={board.ich} zu={() => setNeuerKontakt(false)}
          angelegt={(k) => { setNeuerKontakt(false); oeffneKontakt(k.id) }} />
      )}
      {phasenOffen && <PhasenEditor zu={() => setPhasenOffen(false)} />}
      {offenerDeal && board && (
        <DealBlatt
          dealId={offenerDeal}
          board={board}
          zu={() => oeffne(null)}
          oeffneKontakt={(id) => oeffneKontakt(id)}
        />
      )}
      {offenerKontakt && board && (
        <KontaktBlatt
          kontaktId={offenerKontakt}
          board={board}
          zu={() => oeffneKontakt(null)}
          oeffneDeal={(id) => oeffne(id)}
          neuerDeal={(k) => setNeu({ stufe: offeneStufen.find((x) => x.art === "offen")?.schluessel || "lead", kontakt: k })}
        />
      )}
    </div>
  )
}

// ── Kennzahlen ───────────────────────────────────────────────────────────

function Kennzahlen({ deals, stufeVon, erinnerungen }: {
  deals: Deal[]; stufeVon: Record<string, Stufe>; erinnerungen: Erinnerung[]
}) {
  const offen = deals.filter((d) => stufeVon[d.stufe]?.art === "offen")
  const monat = offen.reduce((s, d) => s + (d.wert_monat || 0), 0)
  const vor30 = Date.now() - 30 * 86_400_000
  const gewonnen = deals.filter((d) => stufeVon[d.stufe]?.art === "gewonnen" && new Date(d.stufe_seit).getTime() >= vor30)
  const verloren = deals.filter((d) => stufeVon[d.stufe]?.art === "verloren" && new Date(d.stufe_seit).getTime() >= vor30)
  const quote = gewonnen.length + verloren.length ? Math.round((gewonnen.length / (gewonnen.length + verloren.length)) * 100) : null
  const dran = erinnerungen.filter((e) => e.schwere !== "grau").length
  const ueber = erinnerungen.filter((e) => e.schwere === "rot").length
  return (
    <div className="vt-zahlen">
      <div className="vt-zahl">
        <span>Offene Deals</span>
        <strong>{offen.length}</strong>
        <small>{offen.filter((d) => d.stufe === "lead").length} neue Leads</small>
      </div>
      <div className="vt-zahl">
        <span>Pipeline</span>
        <strong>{euro(monat)}<small style={{ fontSize: 14, fontWeight: 500 }}> /Monat</small></strong>
        <small>{euro(offen.reduce((s, d) => s + (d.wert_einmalig || 0), 0))} einmalig</small>
      </div>
      <div className="vt-zahl">
        <span>Gewonnen · 30 Tage</span>
        <strong>{gewonnen.length}</strong>
        <small>{quote == null ? "noch keine Abschlüsse" : `${quote} % Abschlussquote`}</small>
      </div>
      <div className={`vt-zahl ${ueber ? "alarm" : ""}`}>
        <span>Heute dran</span>
        <strong>{dran}</strong>
        <small>{ueber ? `${ueber} überfällig` : dran ? "alles im Plan" : "nichts offen 🎉"}</small>
      </div>
    </div>
  )
}

// ── Heute dran ───────────────────────────────────────────────────────────

function HeuteDran({ erinnerungen, oeffne }: { erinnerungen: Erinnerung[]; oeffne: (id: number) => void }) {
  const qc = useQueryClient()
  const wichtig = erinnerungen.filter((e) => e.schwere !== "grau")
  const ohneSchritt = erinnerungen.filter((e) => e.art === "kein_schritt")
  // Zu bleibt nur für heute zu: morgen meldet sich der Reiter wieder.
  const [zu, setZu] = useState(lies(FOKUS_SPEICHER) === heuteIso())
  const [alleOhne, setAlleOhne] = useState(false)

  useEffect(() => { if (wichtig.length === 0) setAlleOhne(false) }, [wichtig.length])

  const aktualisiere = () => {
    qc.invalidateQueries({ queryKey: ["crm-board"] })
    qc.invalidateQueries({ queryKey: ["crm-faellig"] })
  }
  const aufgabe = useMutation({
    mutationFn: ({ id, daten }: { id: number; daten: Record<string, unknown> }) =>
      crmFetch(`/aufgaben/${id}`, { method: "PATCH", json: daten }),
    onSuccess: aktualisiere,
    onError: (e: Error) => toast(e.message, "error"),
  })
  const deal = useMutation({
    mutationFn: ({ id, daten }: { id: number; daten: Record<string, unknown> }) =>
      crmFetch(`/deals/${id}`, { method: "PATCH", json: daten }),
    onSuccess: aktualisiere,
    onError: (e: Error) => toast(e.message, "error"),
  })

  const morgen = (e: Erinnerung) => {
    const tag = plusTage(heuteIso(), 1)
    if (e.aufgabe_id) aufgabe.mutate({ id: e.aufgabe_id, daten: { faellig_am: tag } })
    else if (e.art === "schritt") deal.mutate({ id: e.deal_id, daten: { naechster_schritt_am: tag } })
    toast("Auf morgen verschoben")
  }

  if (zu) {
    if (!wichtig.length) return null
    return (
      <button className="vt-knopf leise klein" style={{ alignSelf: "flex-start" }}
        onClick={() => { setZu(false); schreib(FOKUS_SPEICHER, "") }}>
        <Sparkles /> {wichtig.length} {wichtig.length === 1 ? "Sache" : "Sachen"} heute dran <ChevronDown />
      </button>
    )
  }

  const liste = alleOhne ? [...wichtig, ...ohneSchritt] : wichtig
  return (
    <section className="vt-fokus" aria-label="Heute dran">
      <div className="vt-fokus-kopf">
        <Sparkles size={18} color="var(--accent-color)" />
        <h3>Heute dran</h3>
        <span className={`vt-pille ${wichtig.some((e) => e.schwere === "rot") ? "rot" : wichtig.length ? "gelb" : "gruen"}`}>
          {wichtig.length || "alles erledigt"}
        </span>
        <button className="vt-ikon" title="Für heute einklappen" onClick={() => { setZu(true); schreib(FOKUS_SPEICHER, heuteIso()) }}>
          <ChevronUp />
        </button>
      </div>
      {liste.length === 0 ? (
        <div className="vt-fokus-leer"><Check size={16} color="var(--vt-gruen)" /> Nichts fällig. Zeit für neue Leads.</div>
      ) : (
        <ul className="vt-fokus-liste">
          {liste.slice(0, 12).map((e, i) => (
            <li key={`${e.deal_id}-${e.art}-${e.aufgabe_id || i}`} className="vt-fokus-eintrag">
              <span className={`vt-fokus-punkt ${e.schwere}`} />
              <div className="vt-fokus-text">
                <b>{e.text}</b>
                <span>{e.deal_titel}{e.zustaendig ? ` · ${e.zustaendig}` : ""}</span>
              </div>
              <div className="vt-fokus-aktionen">
                {e.aufgabe_id && (
                  <button className="vt-ikon ok" title="Erledigt"
                    onClick={() => { aufgabe.mutate({ id: e.aufgabe_id!, daten: { erledigt: true } }); toast("Erledigt ✓") }}>
                    <Check />
                  </button>
                )}
                {(e.aufgabe_id || e.art === "schritt") && (
                  <button className="vt-ikon" title="Auf morgen" onClick={() => morgen(e)}><Clock /></button>
                )}
                <button className="vt-ikon" title="Deal öffnen" onClick={() => oeffne(e.deal_id)}><ArrowRight /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {ohneSchritt.length > 0 && !alleOhne && (
        <div className="vt-fokus-fuss">
          {ohneSchritt.length} {ohneSchritt.length === 1 ? "Deal hat" : "Deals haben"} keinen nächsten Schritt ·{" "}
          <button onClick={() => setAlleOhne(true)}>anzeigen</button>
        </div>
      )}
    </section>
  )
}

// ── Board ────────────────────────────────────────────────────────────────

function BoardAnsicht({ stufen, deals, dringend, oeffne, verschiebe, neuIn }: {
  stufen: Stufe[]; deals: Deal[]; dringend: Set<number>
  oeffne: (id: number) => void; verschiebe: (id: number, stufe: string) => void; neuIn: (stufe: string) => void
}) {
  const [zieht, setZieht] = useState<number | null>(null)
  const [ziel, setZiel] = useState<string | null>(null)

  const proStufe = useMemo(() => {
    const m: Record<string, Deal[]> = {}
    for (const d of deals) (m[d.stufe] ||= []).push(d)
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => (naechsterTermin(a) || "9999").localeCompare(naechsterTermin(b) || "9999"))
    }
    return m
  }, [deals])

  const fallen = (stufe: string) => (ev: React.DragEvent) => {
    ev.preventDefault()
    const id = Number(ev.dataTransfer.getData("text/plain"))
    setZiel(null); setZieht(null)
    const d = deals.find((x) => x.id === id)
    if (d && d.stufe !== stufe) verschiebe(id, stufe)
  }
  const ueber = (stufe: string) => (ev: React.DragEvent) => {
    ev.preventDefault()
    ev.dataTransfer.dropEffect = "move"
    if (ziel !== stufe) setZiel(stufe)
  }

  return (
    <>
      <div className="vt-board">
        {stufen.filter((s) => s.art !== "verloren").map((s) => {
          const liste = proStufe[s.schluessel] || []
          const summe = liste.reduce((x, d) => x + (d.wert_monat || 0), 0)
          return (
            <div key={s.schluessel}
              className={`vt-spalte ${ziel === s.schluessel ? "ziel" : ""}`}
              onDragOver={ueber(s.schluessel)}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setZiel(null) }}
              onDrop={fallen(s.schluessel)}
            >
              <div className="vt-spalte-kopf">
                <span className="vt-punkt" style={{ background: s.farbe || "#94a3b8" }} />
                <b title={s.label}>{s.label}</b>
                <span className="vt-anzahl">{liste.length}</span>
              </div>
              <div className="vt-spalte-summe">{summe ? `${euro(summe)} /Monat` : ""}</div>
              <div className="vt-spalte-karten">
                {liste.map((d) => (
                  <DealKarte key={d.id} deal={d} dringend={dringend.has(d.id)} gewonnen={s.art === "gewonnen"}
                    zieht={zieht === d.id}
                    onDragStart={(ev) => { ev.dataTransfer.setData("text/plain", String(d.id)); ev.dataTransfer.effectAllowed = "move"; setZieht(d.id) }}
                    onDragEnd={() => { setZieht(null); setZiel(null) }}
                    onClick={() => oeffne(d.id)} />
                ))}
                {liste.length === 0 && <div className="vt-spalte-leer">Hierher ziehen</div>}
              </div>
              {s.art === "offen" && (
                <button className="vt-spalte-neu" onClick={() => neuIn(s.schluessel)}><Plus /> Deal</button>
              )}
            </div>
          )
        })}
      </div>
      {zieht && (
        <div className="vt-abwurf">
          <div className={`verloren ${ziel === "verloren" ? "ziel" : ""}`}
            onDragOver={ueber("verloren")} onDragLeave={() => setZiel(null)} onDrop={fallen("verloren")}>
            <X /> Verloren
          </div>
          <div className={`gewonnen ${ziel === "__gewonnen" ? "ziel" : ""}`}
            onDragOver={ueber("__gewonnen")} onDragLeave={() => setZiel(null)}
            onDrop={(ev) => {
              const g = stufen.find((x) => x.art === "gewonnen")
              if (g) fallen(g.schluessel)(ev)
            }}>
            <Trophy /> Gewonnen
          </div>
        </div>
      )}
    </>
  )
}

function DealKarte({ deal, dringend, gewonnen, zieht, onClick, onDragStart, onDragEnd }: {
  deal: Deal; dringend: boolean; gewonnen: boolean; zieht: boolean
  onClick: () => void; onDragStart: (e: React.DragEvent) => void; onDragEnd: () => void
}) {
  const termin = naechsterTermin(deal)
  const heute = heuteIso()
  const terminArt = !termin ? null : termin < heute ? "rot" : termin === heute ? "gelb" : ""
  const tage = tageSeit(deal.stufe_seit)
  return (
    <button
      className={`vt-karte ${dringend ? "dringend" : ""} ${zieht ? "zieht" : ""}`}
      draggable onDragStart={onDragStart} onDragEnd={onDragEnd} onClick={onClick}
    >
      <div className="vt-karte-oben">
        <div className="vt-karte-titel">
          <b>{deal.titel}</b>
          {deal.ansprechpartner && <span>{deal.ansprechpartner}</span>}
        </div>
        {deal.zustaendig && (
          <span className="vt-avatar" style={{ background: personFarbe(deal.zustaendig) }} title={deal.zustaendig}>
            {initialen(deal.zustaendig)}
          </span>
        )}
      </div>
      {(deal.wert_monat || deal.wert_einmalig) ? (
        <div className="vt-karte-wert">
          {deal.wert_monat ? <>{euro(deal.wert_monat)}<small> /Monat</small></> : null}
          {deal.wert_monat && deal.wert_einmalig ? <small> · </small> : null}
          {deal.wert_einmalig ? <small>{euro(deal.wert_einmalig)} einmalig</small> : null}
        </div>
      ) : null}
      {!gewonnen && (
        <div className="vt-karte-unten">
          {termin ? (
            <span className={`vt-pille ${terminArt}`}>
              <Clock size={12} /> {tagKurz(termin)}
            </span>
          ) : (
            <span className="vt-pille offen">Kein nächster Schritt</span>
          )}
          <span className="vt-alter" title="So lange schon in dieser Stufe">{tage === 0 ? "neu" : `${tage} T`}</span>
        </div>
      )}
    </button>
  )
}

// ── Liste ────────────────────────────────────────────────────────────────

function ListenAnsicht({ deals, stufeVon, oeffne }: {
  deals: Deal[]; stufeVon: Record<string, Stufe>; oeffne: (id: number) => void
}) {
  const sortiert = [...deals].sort((a, b) =>
    (stufeVon[a.stufe]?.position ?? 99) - (stufeVon[b.stufe]?.position ?? 99) || a.titel.localeCompare(b.titel))
  return (
    <div className="vt-liste">
      <table>
        <thead>
          <tr>
            <th>Firma</th><th>Stufe</th><th>Wert /Monat</th><th>Zuständig</th><th>Nächster Schritt</th><th>Letzte Aktivität</th>
          </tr>
        </thead>
        <tbody>
          {sortiert.map((d) => {
            const s = stufeVon[d.stufe]
            const termin = naechsterTermin(d)
            const heute = heuteIso()
            return (
              <tr key={d.id} onClick={() => oeffne(d.id)}>
                <td><b>{d.titel}</b>{d.ansprechpartner && <div className="vt-hinweis">{d.ansprechpartner}</div>}</td>
                <td><span className="vt-pille"><span className="vt-punkt" style={{ background: s?.farbe || "#94a3b8" }} />{s?.label || d.stufe}</span></td>
                <td>{euro(d.wert_monat)}</td>
                <td className="leise">{d.zustaendig || "–"}</td>
                <td>
                  {termin
                    ? <span className={`vt-pille ${termin < heute ? "rot" : termin === heute ? "gelb" : ""}`}>{tagKurz(termin)}{d.naechster_schritt ? ` · ${d.naechster_schritt}` : ""}</span>
                    : <span className="vt-hinweis">–</span>}
                </td>
                <td className="leise">{new Date(d.letzte_aktivitaet).toLocaleDateString("de-DE")}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ── Neuer Deal ───────────────────────────────────────────────────────────

function NeuerDeal({ stufe, kontakt: vorgewaehlt, stufen, ich, zu, angelegt }: {
  stufe: string; kontakt?: Kontakt; stufen: Stufe[]; ich: string; zu: () => void; angelegt: (d: Deal) => void
}) {
  const qc = useQueryClient()
  const [kontakt, setKontakt] = useState<Kontakt | null>(vorgewaehlt || null)
  const [f, setF] = useState({
    titel: vorgewaehlt?.firma || "", ansprechpartner: "", email: "", telefon: "", wert_monat: "", wert_einmalig: "",
    quelle: vorgewaehlt?.quelle || "", stufe, zustaendig: ich,
  })
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF({ ...f, [k]: e.target.value })
  const zust = useQuery<{ items: string[] }>({
    queryKey: ["portal-aufgaben-zustaendige"],
    queryFn: async () => (await fetch("/api/v1/portal-aufgaben/zustaendige", { credentials: "include" })).json(),
    staleTime: 600_000,
  })
  const anlegen = useMutation({
    mutationFn: () => crmFetch<Deal>("/deals", {
      method: "POST",
      json: {
        ...f,
        ...(kontakt ? { kontakt_id: kontakt.id, ansprechpartner: undefined, email: undefined, telefon: undefined } : {}),
        wert_monat: f.wert_monat ? Number(f.wert_monat.replace(",", ".")) : null,
        wert_einmalig: f.wert_einmalig ? Number(f.wert_einmalig.replace(",", ".")) : null,
      },
    }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["crm-board"] })
      qc.invalidateQueries({ queryKey: ["crm-kontakte"] })
      qc.invalidateQueries({ queryKey: ["crm-faellig"] })
      toast(`„${d.titel}" angelegt`)
      angelegt(d)
    },
    onError: (e: Error) => toast(e.message, "error"),
  })

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") zu() }
    window.addEventListener("keydown", esc)
    return () => window.removeEventListener("keydown", esc)
  }, [zu])

  return createPortal(
    <div className="vt-portal">
      <div className="vt-schleier" onClick={zu} />
      <form className="vt-dialog" role="dialog" aria-label="Neuer Deal"
        onSubmit={(e) => { e.preventDefault(); if (f.titel.trim()) anlegen.mutate() }}>
        <div className="vt-dialog-kopf">
          <h3>Neuer Deal</h3>
          <p>Firma reicht – den Rest kannst du jederzeit ergänzen.</p>
        </div>
        <div className="vt-dialog-rumpf">
          <div className="vt-felder">
            <div className="vt-feld breit">
              <label>Kontakt</label>
              <KontaktWahl gewaehlt={kontakt} waehle={(k) => {
                setKontakt(k)
                if (k && !f.titel.trim()) setF({ ...f, titel: k.firma || k.name || "" })
              }} />
            </div>
            {!kontakt && (
              <>
                <div className="vt-feld breit"><div className="vt-oder">oder neuen Kontakt gleich mit anlegen</div></div>
                <div className="vt-feld"><label>Ansprechperson</label><input value={f.ansprechpartner} onChange={set("ansprechpartner")} /></div>
                <div className="vt-feld"><label>Telefon</label><input value={f.telefon} onChange={set("telefon")} type="tel" /></div>
                <div className="vt-feld breit"><label>E-Mail</label><input value={f.email} onChange={set("email")} type="email" /></div>
              </>
            )}
            <div className="vt-feld breit">
              <label>Deal / Firma *</label>
              <input autoFocus={!vorgewaehlt} value={f.titel} onChange={set("titel")} placeholder="z. B. Hisar Dachfenster" />
            </div>
            <div className="vt-feld"><label>Wert pro Monat (€)</label><input value={f.wert_monat} onChange={set("wert_monat")} inputMode="decimal" placeholder="0" /></div>
            <div className="vt-feld"><label>Einmalig (€)</label><input value={f.wert_einmalig} onChange={set("wert_einmalig")} inputMode="decimal" placeholder="0" /></div>
            <div className="vt-feld">
              <label>Stufe</label>
              <select value={f.stufe} onChange={set("stufe")}>
                {stufen.filter((s) => s.art === "offen").map((s) => <option key={s.schluessel} value={s.schluessel}>{s.label}</option>)}
              </select>
            </div>
            <div className="vt-feld">
              <label>Quelle</label>
              <select value={f.quelle} onChange={set("quelle")}>
                <option value="">–</option>
                {QUELLEN.map((q) => <option key={q}>{q}</option>)}
              </select>
            </div>
            <div className="vt-feld breit">
              <label>Zuständig</label>
              <select value={f.zustaendig} onChange={set("zustaendig")}>
                <option value="">niemand</option>
                {Array.from(new Set([ich, ...(zust.data?.items || [])].filter(Boolean))).map((n) => <option key={n}>{n}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="vt-dialog-fuss">
          <button type="button" className="vt-knopf leise" onClick={zu}>Abbrechen</button>
          <button type="submit" className="vt-knopf haupt" disabled={!f.titel.trim() || anlegen.isPending}>
            {anlegen.isPending ? "Legt an …" : "Deal anlegen"}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  )
}
